import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useAssetApi } from "../api/index.ts";
import { assetQuery, type AssetQueryCacheData } from "./assetQuery.ts";

// Convert a Blob to an "Object URL" (e.g. "blob:{uuid}") and ensures it's correctly
// released when it's updated or unmounted. (Generic, could be put somewhere else)
const useBlobUrl = (blob: Blob | null | undefined): string | null => {
  const url = useMemo(() => {
    return blob ? URL.createObjectURL(blob) : null;
  }, [blob]);

  useEffect(() => {
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [url]);

  return url;
};

export const useAssetBlob = (assetId: string | undefined): Blob | null => {
  const api = useAssetApi();
  const result = useQuery(assetQuery(api, assetId));
  return result.data ? result.data.blob : null;
};

export const useAssetUrl = (assetId: string | undefined): string | null => {
  const blob = useAssetBlob(assetId);
  return useBlobUrl(blob);
};

// Return a map from asset id to resolved asset object urls. Any assets that were
// not found are not present in the map.
// Note these URLs are temporary "Object URLs" that are revoked on assetIds changing.
export const useAssetUrlMap = (assetIds: readonly string[]): ReadonlyMap<string, string> => {
  const api = useAssetApi();

  const queries = useMemo(() => assetIds.map((assetId) => assetQuery(api, assetId)), [api, assetIds]);

  // Can't just use combine to get the object URLs, as react-query (deliberately) doesn't
  // do unmount, and we want to revoke the object URLs on unmount.
  const queryResults = useQueries({ queries });

  // useQueries() always returns a new array every render, so stabilise the derived data by
  // value: only adopt a new array when the cached values actually change. Adjusting state during
  // render (React re-renders immediately, before committing) avoids the cascading-render effect.
  const newData = queryResults.map((result) => result.data ?? null);
  const [stableData, setStableData] = useState<ReadonlyArray<AssetQueryCacheData | null>>(newData);
  if (!arrayEquals(newData, stableData)) {
    setStableData(newData);
  }

  const result = useMemo(() => {
    const map = new Map<string, string>();

    for (const item of stableData) {
      if (item?.blob) {
        const url = URL.createObjectURL(item.blob);
        map.set(item.assetId, url);
      }
    }

    return map;
  }, [stableData]);

  useEffect(() => {
    return () => {
      for (const url of result.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [result]);

  return result;
};

const arrayEquals = <T>(a: readonly T[], b: readonly T[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};
